import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { toastApiError } from "@/lib/apiError";

const C = { panel: "#0A2826", teal: "#0A8B85", tealSoft: "#7BD3CE", orange: "#F59E0B", red: "#F87171", green: "#2CC5A8" };

const fmtDt = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return isNaN(d) ? "—" : d.toLocaleDateString("en-GB", { day: "numeric", month: "short" }) +
    " · " + d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
};

const Tile = ({ label, value, accent }) => (
  <div className="rounded-lg px-4 py-3" style={{ backgroundColor: C.panel, border: "1px solid rgba(123,211,206,0.14)" }}>
    <div className="text-[10px] uppercase tracking-[0.18em] text-white/40 font-semibold">{label}</div>
    <div className="text-xl font-extrabold mt-1" style={{ color: accent || "#fff" }}>{value}</div>
  </div>
);

export default function FmModelsPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/fm/stats");
      setData(data);
    } catch (e) { toastApiError(e, "Could not load model stats"); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  return (
    <div className="space-y-5" data-testid="admin-fm-panel">
      <p className="text-sm text-white/50 max-w-xl">
        Financial Model Builder monetisation — exports, monthly refreshes and the paid advisor-review queue. Reviews should be actioned within one business day.
      </p>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3" data-testid="fm-stats-tiles">
        <Tile label="Models built" value={data?.total_models ?? "—"} />
        <Tile label="Exports unlocked" value={data?.exported ?? "—"} accent={C.tealSoft} />
        <Tile label="Export credits spent" value={data?.export_credits_spent ?? "—"} accent={C.orange} />
        <Tile label="Monthly refreshes" value={data?.monthly_refreshes ?? "—"} accent={C.tealSoft} />
        <Tile label="Review revenue" value={data ? `$${(data.review_revenue_usd || 0).toLocaleString()}` : "—"} accent={C.green} />
      </div>

      <div className="rounded-lg p-4" style={{ backgroundColor: C.panel, border: "1px solid rgba(123,211,206,0.14)" }}>
        <div className="flex items-center justify-between mb-3">
          <div className="text-sm font-extrabold text-white">Paid model reviews — action queue</div>
          <button onClick={load} data-testid="fm-review-refresh"
            className="text-xs font-semibold px-3 py-1.5 rounded-md text-white" style={{ backgroundColor: C.teal }}>
            Refresh
          </button>
        </div>
        {loading ? (
          <div className="text-sm text-white/40 py-4">Loading…</div>
        ) : (data?.review_queue || []).length === 0 ? (
          <div className="text-sm text-white/40 py-2" data-testid="fm-review-empty">
            No paid model reviews waiting. When a founder pays $175 for a review it appears here.
          </div>
        ) : (
          <table className="w-full text-[12px]" data-testid="fm-review-queue">
            <thead>
              <tr style={{ color: "rgba(255,255,255,0.5)" }}>
                {["Founder", "Model", "Sector", "Paid", "Status", ""].map((h) => (
                  <th key={h} className="text-left py-2 font-semibold uppercase tracking-wider">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.review_queue.map((r) => <ReviewRow key={r.fm_id} row={r} onChanged={load} />)}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function ReviewRow({ row, onChanged }) {
  const [busy, setBusy] = useState(false);
  const status = row.advisor_review_status || "requested";
  const next = status === "requested" ? "in_progress" : "completed";
  const setStatus = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/fm/${row.fm_id}/review-status`, { status: next });
      toast.success(next === "completed" ? "Review marked completed" : "Review marked in progress");
      onChanged();
    } catch (e) { toastApiError(e, "Could not update status"); }
    setBusy(false);
  };
  return (
    <tr className="border-t border-white/5" data-testid={`fm-review-row-${row.fm_id}`}>
      <td className="py-2 pr-3">
        <div className="text-white font-medium">{row.user_name || "—"}</div>
        <div className="text-white/40">{row.user_email}</div>
      </td>
      <td className="py-2 pr-3 text-white/70">{row.name || row.fm_id}</td>
      <td className="py-2 pr-3 text-white/50">{row.sector || "—"} · {row.revenue_model || ""}</td>
      <td className="py-2 pr-3 text-white/50 whitespace-nowrap">{fmtDt(row.advisor_review_paid_at)}</td>
      <td className="py-2 pr-3">
        <span className="text-[10px] font-bold px-2 py-1 rounded-full whitespace-nowrap"
          style={{
            backgroundColor: status === "in_progress" ? "rgba(245,158,11,0.15)" : "rgba(248,113,113,0.15)",
            color: status === "in_progress" ? C.orange : C.red,
          }}>
          {status === "in_progress" ? "IN PROGRESS" : "REQUESTED"}
        </span>
      </td>
      <td className="py-2 text-right">
        <button onClick={setStatus} disabled={busy} data-testid={`fm-review-advance-${row.fm_id}`}
          className="text-[11px] font-bold px-3 py-1.5 rounded-md disabled:opacity-50"
          style={{ backgroundColor: next === "completed" ? "rgba(44,197,168,0.15)" : "rgba(245,158,11,0.15)",
                   border: `1px solid ${next === "completed" ? "rgba(44,197,168,0.4)" : "rgba(245,158,11,0.4)"}`,
                   color: next === "completed" ? C.green : C.orange }}>
          {busy ? "…" : next === "completed" ? "Mark completed" : "Start review"}
        </button>
      </td>
    </tr>
  );
}
